import { computed, onMounted, readonly, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useFilterCatalog } from "@/shared/composables/useFilterCatalog";
import { useAppToast } from "@/shared/composables/useAppToast";
import { displayDateToIsoDay, displayDateToLocalMidnightIso, toDatePickerFormat, } from "@/shared/utils/date-formatting";
import { quickFiltersToAst } from "@/shared/utils/quickFilters";
import { QUICK_FILTERS_STORAGE_PREFIX as STORAGE_KEY_PREFIX } from "@/shared/utils/listBrowsingStorage";
import { useProfileStore } from "@/modules/settings/public";
import { logger } from "@/infrastructure/services/LoggerService";
/** Codes the list maps to a message only while chips are active: without them the user has nothing to fix (R70). */
const QUICK_FILTER_ERROR_KEYS = {
    "Opqry.Ast.Invalid": "filters.quick.tooComplex",
    "Opqry.Value.Invalid": "filters.quick.valueInvalid",
};
/**
 * The format the date editors resolve, saved with each date chip: a chip restored under another one reads another day.
 * The locale counts only through the fallback it selects, so a language switch under a profile format keeps the chips.
 */
function dateFormatKey(dates) {
    return toDatePickerFormat(dates.userDateFormat, dates.locale);
}
/**
 * The OPQRY field catalog `fetchCatalog` returns, with a value editor per field type, loaded on mount (a failure
 * toasts), plus the options `useTableQuery` needs to label the chips. Call it before `useTableQuery`.
 *
 * Option loaders run alongside the catalog, so a chip restored from the tab is labelled by the time `loaded` settles
 * and the list's first fetch already carries it. A failed loader leaves its field unoffered for this visit, without a
 * toast, and adds its path to `failedOptionPaths`. The user profile is awaited the same way, for its date format, with
 * the same caveat: a profile request that hangs holds back `loaded`.
 */
export function useQuickFilterCatalog(fetchCatalog, options) {
    const { t, locale } = useI18n();
    const profileStore = useProfileStore();
    const { toastError } = useAppToast();
    const failedOptionPaths = ref(new Set());
    async function loadListItems() {
        const failed = [];
        const entries = await Promise.all(Object.entries(options.options).map(async ([path, source]) => {
            if (typeof source !== "function")
                return [path, source.map((v) => ({ id: v.id, label: t(v.labelKey) }))];
            try {
                return [path, [...(await source())]];
            }
            catch (e) {
                failed.push(path);
                logger.error(`useQuickFilterCatalog: could not load the options of ${path}`, e);
                return [path, []];
            }
        }));
        failedOptionPaths.value = new Set(failed);
        return Object.fromEntries(entries);
    }
    /**
     * The NavBar fetches the profile alongside this catalog, so the date format waits for it: read before it lands, the
     * format falls back to the locale's (`mm/dd/yyyy` in English), where every day above 12 is rejected. A failed fetch
     * keeps that fallback for this visit.
     */
    const dateFormat = ref(null);
    async function loadProfile() {
        try {
            return await profileStore.fetchProfile();
        }
        catch (e) {
            logger.error("useQuickFilterCatalog: could not load the profile, dates fall back to the locale format", e);
            return null;
        }
    }
    const catalog = useFilterCatalog(fetchCatalog, { rootGroupLabel: options.rootGroupLabel, relatedGroupLabel: () => t("filters.groups.related"), t }, async () => {
        const [listItems, profile] = await Promise.all([loadListItems(), loadProfile()]);
        const dates = { userDateFormat: profile?.dateFormat, locale: locale.value, profileLoaded: profile !== null };
        dateFormat.value = dates;
        return {
            listItems,
            booleanItems: [
                { id: "true", label: t("filters.quick.yes") },
                { id: "false", label: t("filters.quick.no") },
            ],
            rangeBounds: options.rangeBounds,
            textPlaceholder: t("filters.quick.textPlaceholder"),
            rangeSeparatorLabel: t("filters.quick.rangeSeparator"),
            rangeErrorLabel: t("filters.quick.rangeError"),
            rangeMinLabel: t("filters.quick.rangeMin"),
            rangeMaxLabel: t("filters.quick.rangeMax"),
            dateFormat: toDatePickerFormat(dates.userDateFormat, dates.locale),
            dateLanguage: dates.locale,
            dateFromLabel: t("filters.quick.dateFrom"),
            dateToLabel: t("filters.quick.dateTo"),
            selectAllLabel: t("table.selectAll"),
            step2SearchPlaceholder: t("filters.quick.searchValue"),
        };
    });
    /** True once the catalog load has settled, failed or not. */
    const loaded = ref(false);
    onMounted(async () => {
        await catalog.load();
        loaded.value = true;
        if (catalog.hasError.value)
            toastError("filters.loadError");
    });
    const fields = () => catalog.fields.value;
    const tableQueryOptions = {
        filterLabels: () => Object.fromEntries(fields().map((f) => [f.key, f.label])),
        filterItems: () => Object.fromEntries(fields().flatMap((f) => (f.items ? [[f.key, f.items]] : []))),
        filterFieldConfigs: () => Object.fromEntries(fields().map((f) => [
            f.key,
            f.type === "range"
                ? {
                    rangeConfig: {
                        separatorLabel: f.rangeSeparatorLabel,
                        ...(f.rangeMin !== undefined && { min: f.rangeMin, max: f.rangeMax, step: f.rangeStep }),
                    },
                }
                : f.type === "dateRange"
                    ? {
                        dateRangeConfig: {
                            format: f.dateFormat,
                            fromLabel: f.dateFromLabel,
                            toLabel: f.dateToLabel,
                        },
                    }
                    : { placeholder: f.placeholder },
        ])),
    };
    return { ...catalog, loaded, failedOptionPaths, dateFormat: readonly(dateFormat), tableQueryOptions };
}
/** The saved entry's field holding `dateFormatKey` per date chip; not a chip, so `readSavedFilters` skips it. */
const DATE_FORMATS_FIELD = "$dateFormats";
function readSavedFilters(storageKey) {
    try {
        const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${storageKey}`);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
            return null;
        const entries = Object.entries(parsed).filter(([, value]) => !!value && typeof value === "object" && typeof value.type === "string");
        if (entries.length === 0)
            return null;
        const formats = parsed[DATE_FORMATS_FIELD];
        const dateFormats = formats && typeof formats === "object" && !Array.isArray(formats)
            ? Object.fromEntries(Object.entries(formats).filter(([, format]) => typeof format === "string"))
            : {};
        return { filters: Object.fromEntries(entries), dateFormats };
    }
    catch (e) {
        logger.error(`useQuickFilters: could not read the saved quick filters of ${storageKey}`, e);
        return null;
    }
}
function writeSavedFilters(storageKey, filters, dateFormats) {
    try {
        const key = `${STORAGE_KEY_PREFIX}${storageKey}`;
        if (Object.keys(filters).length === 0)
            sessionStorage.removeItem(key);
        else if (Object.keys(dateFormats).length === 0)
            sessionStorage.setItem(key, JSON.stringify(filters));
        else
            sessionStorage.setItem(key, JSON.stringify({ ...filters, [DATE_FORMATS_FIELD]: dateFormats }));
    }
    catch (e) {
        logger.error(`useQuickFilters: could not save the quick filters of ${storageKey}`, e);
    }
}
/**
 * Turns the active chips into the `refinement` the BFF ANDs with the view or inline AST.
 *
 * The chips are kept for the browser tab under `storageKey` (sessionStorage, like the detail-page navigation context),
 * so coming back from a detail page restores them; `ready` stays false until the settled catalog has translated them
 * (or dropped those it no longer offers), so the list's first load already carries them.
 *
 * `dateOnlyFields`: paths holding a calendar date (OPHUB `DateOnly`), filtered by `YYYY-MM-DD` day bounds. OPQRY types
 * them `DateTime` like the instant columns; a date-only path missing here gets instant bounds, which OPQRY truncates
 * to the UTC date — the wrong day east of UTC.
 */
export function useQuickFilters(tableQuery, catalog, options) {
    const { descriptors } = catalog;
    const dateOnlyFields = options.dateOnlyFields ?? new Set();
    // The days are parsed with the format the date editors were built with, never the live profile or locale: a profile
    // or language that changes while the list is mounted would otherwise reject every day the editor accepted.
    const toDateBounds = (from, to, field) => {
        const dates = catalog.dateFormat.value;
        if (!dates)
            return {};
        const convert = dateOnlyFields.has(field) ? displayDateToIsoDay : displayDateToLocalMidnightIso;
        return {
            from: convert(from, dates.userDateFormat, dates.locale, 0),
            before: convert(to, dates.userDateFormat, dates.locale, 1),
        };
    };
    const saved = readSavedFilters(options.storageKey);
    for (const [key, value] of Object.entries(saved?.filters ?? {}))
        tableQuery.setFilter(key, value);
    // Saving waits for the restored chips to settle, and stays off when the catalog failed to load: that failure hides
    // them for this visit only, so it must not erase them from the tab. Likewise every save carries the restored chips
    // hidden for this visit (option loader or profile failed), until the user empties the panel: kept past that, they
    // would come back unseen. Each date chip is saved with the format it was typed in, a hidden one with its own.
    const saving = ref(!saved);
    let hiddenSaved = {};
    let hiddenDateFormats = {};
    let hadChips = false;
    watch(() => tableQuery.state.filters, (filters) => {
        const hasChips = Object.keys(filters ?? {}).length > 0;
        if (hadChips && !hasChips) {
            hiddenSaved = {};
            hiddenDateFormats = {};
        }
        hadChips = hasChips;
        if (!saving.value)
            return;
        const dates = catalog.dateFormat.value;
        const dateFormats = { ...hiddenDateFormats };
        for (const [key, value] of Object.entries(filters ?? {})) {
            if (value.type === "dateRange" && dates)
                dateFormats[key] = dateFormatKey(dates);
        }
        writeSavedFilters(options.storageKey, { ...hiddenSaved, ...filters }, dateFormats);
    }, { deep: true });
    // A restored chip quickFiltersToAst cannot translate would stay on screen unapplied: it leaves the screen before the
    // first load, and leaves the tab too when the loaded catalog no longer offers its field. A date chip saved under
    // another date format (or before formats were saved) would parse to another day, or to none: it leaves the screen,
    // and the tab too unless the profile failed to load, the one case where the format differs for this visit only.
    const restored = ref(!saved);
    watch(catalog.loaded, (loaded) => {
        if (!loaded || restored.value)
            return;
        const known = new Set(catalog.hasError.value ? [] : descriptors.value.map((d) => d.path));
        const restoredChips = Object.entries(saved?.filters ?? {});
        const unknown = restoredChips.filter(([key]) => !known.has(key));
        for (const [key] of unknown)
            tableQuery.removeFilter(key);
        hiddenSaved = Object.fromEntries(unknown.filter(([key]) => catalog.failedOptionPaths.value.has(key)));
        const dates = catalog.dateFormat.value;
        const current = dates ? dateFormatKey(dates) : null;
        for (const [key, value] of restoredChips) {
            if (!known.has(key) || value.type !== "dateRange")
                continue;
            const savedFormat = saved?.dateFormats[key];
            if (savedFormat === current)
                continue;
            tableQuery.removeFilter(key);
            if (savedFormat && dates && !dates.profileLoaded) {
                hiddenSaved[key] = value;
                hiddenDateFormats[key] = savedFormat;
            }
        }
        saving.value = !catalog.hasError.value;
        restored.value = true;
    }, { immediate: true });
    const ready = computed(() => restored.value);
    const refinement = computed(() => quickFiltersToAst(tableQuery.state.filters, descriptors.value, toDateBounds));
    /** OPAL's list editor confirms an array of ids; an empty one removes the chip. */
    function onListFilterApply(key, values) {
        if (values.length > 0)
            tableQuery.setFilter(key, { type: "in", values });
        else
            tableQuery.removeFilter(key);
    }
    /** The i18n key of the list's load-error title for `code`. */
    function loadErrorKey(code, fallbackKey) {
        if (!code)
            return fallbackKey;
        if (refinement.value)
            return QUICK_FILTER_ERROR_KEYS[code] ?? fallbackKey;
        // No chip: the search most likely pushed the view past OPQRY's nesting limit (the code also covers a view broken
        // on its own, which clearing the search then reveals), so the message must not say "remove a filter".
        if (tableQuery.state.search?.trim() && code === "Opqry.Ast.Invalid")
            return "filters.quick.searchTooComplex";
        return fallbackKey;
    }
    return { ready, refinement, onListFilterApply, loadErrorKey };
}
